import React, { useState, useCallback, useRef, useEffect, useMemo } from "react";
import DOMPurify from "dompurify";
import {
  ReactFlow,
  MiniMap,
  Controls,
  Background,
  Panel,
  applyNodeChanges as rfApplyNodeChanges,
  applyEdgeChanges as rfApplyEdgeChanges,
} from "@xyflow/react";
import BoardTabs from "./components/BoardTabs";
import BoardToolbar from "./components/BoardToolbar";
import DeviceCatalog from "./components/DeviceCatalog";
import DeviceNode from "./components/DeviceNode";
import SidebarTabs from "./components/SidebarTabs";
import ConnectionsPanel from "./components/ConnectionsPanel";
import SignalsPanel from "./components/SignalsPanel";
import LabelsPanel from "./components/LabelsPanel";
import PartsListPanel from "./components/PartsListPanel";
import WarningEdge from "./components/WarningEdge";
import InfoEdge from "./components/InfoEdge";
import WarnEdge from "./components/WarnEdge";
import WirelessEdge from "./components/WirelessEdge";
import { EdgeClickContext } from "./components/EdgeClickContext";
import { UpdateBoundaryContext } from "./components/UpdateBoundaryContext";
import { UpdateDeviceLabelContext } from "./components/UpdateDeviceLabelContext";
import { SelectPortContext } from "./components/SelectPortContext";
import SignalLegend from "./components/SignalLegend";
import EmptyStateOverlay from "./components/EmptyStateOverlay";
import EditAsCopyButton from "./components/EditAsCopyButton";
import { syncResponseToBoardState } from "./persistence/serializer";
import useDeviceCatalog from "./hooks/useDeviceCatalog";
import useBoardState from "./hooks/useBoardState";
import usePersistence from "./hooks/usePersistence";
import useCables from "./hooks/useCables";
import useEdgeStyling from "./hooks/useEdgeStyling";
import useRigManagement from "./hooks/useRigManagement";
import useConnections from "./hooks/useConnections";
import { findBestSignalCable } from "./utils/signalCableMatcher";
import useCopyPaste from "./hooks/useCopyPaste";
import usePanToSelection from "./hooks/usePanToSelection";
import { createApiClient } from "./persistence/apiClient";
import SignalBoundaryNode from "./components/SignalBoundaryNode";
import CanvasLabelNode from "./components/CanvasLabelNode";
import { UpdateCanvasLabelContext } from "./components/UpdateCanvasLabelContext";
import { ResizeUndoContext } from "./components/ResizeUndoContext";
import { createDeviceNode, createBoundaryNode, createCanvasLabelNode } from "./utils/nodeFactory";

const nodeTypes = { device: DeviceNode, signalBoundary: SignalBoundaryNode, canvasLabel: CanvasLabelNode };
const edgeTypes = { warning: WarningEdge, warn: WarnEdge, info: InfoEdge, wireless: WirelessEdge };

function ReadOnlyShareLink({ slug }) {
  const [copied, setCopied] = useState(false);
  if (!slug) return null;

  const url = `${window.location.origin}/rigs/${slug}`;
  const handleCopy = () => {
    navigator.clipboard.writeText(url).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <button
      className="board-editor-share-btn"
      onClick={handleCopy}
      type="button"
      aria-label="Copy share link"
      title={copied ? "Copied!" : "Copy share link"}
    >
      {copied ? "\u2713 Copied" : "\u{1F517} Share"}
    </button>
  );
}

const noop = () => {};

export default function BoardEditor({ authenticated = false, csrfToken = null, maxSavedRigs = 2, currentRigCount = 0, isAdmin = false, trackEvent = noop, readOnly = false, initialState = null, initialPartsList = null, devMode = false }) {
  const { devices, loading, error } = useDeviceCatalog();
  const {
    nodes,
    edges,
    canUndo,
    canRedo,
    addDevice,
    addBoundary,
    updateBoundary,
    addCanvasLabel,
    updateCanvasLabel,
    swapCanvasLabelOrder,
    addSystem,
    removeDevice,
    addConnection,
    removeConnection,
    reconnectConnection,
    assignCable,
    addChainCable,
    removeChainCable,
    rigCables,
    applyNodeChanges,
    applyEdgeChanges,
    updateDeviceLabel,
    updateExcludedPort,
    dragEnd,
    undo,
    redo,
    loadState,
  } = useBoardState();

  const apiClient = useMemo(
    () => (authenticated && csrfToken ? createApiClient(csrfToken) : null),
    [authenticated, csrfToken]
  );

  const hasCustomDevices = useMemo(
    () => nodes.some((n) => n.type === "device" && n.data?.device?.private),
    [nodes]
  );

  const { saveStatus, currentRigId, rigs, setCurrentRigId, setRigs, rigNameRef, stateRef, saveNow } =
    usePersistence({
      nodes,
      edges,
      rigCables,
      loadState,
      authenticated,
      apiClient,
      maxSavedRigs,
      readOnly,
    });

  const reactFlowWrapper = useRef(null);
  const reactFlowInstance = useRef(null);
  const preDragSnapshot = useRef(null);
  const preResizeSnapshot = useRef(null);

  const [sidebarTab, setSidebarTab] = useState(() => {
    try { return localStorage.getItem("rig_canvas:sidebar_tab") || "devices"; } catch { return "devices"; }
  });
  useEffect(() => {
    try { localStorage.setItem("rig_canvas:sidebar_tab", sidebarTab); } catch { /* ignore */ }
  }, [sidebarTab]);

  // Single selection: { type: "edge"|"boundary"|"device"|"port", id/key, nodeId?, portId? }
  const [selection, setSelection] = useState(null);

  const { powerCables, signalCables, cablesLoading } = useCables({ readOnly });

  const onNodesChange = useCallback(
    (changes) => {
      const updated = rfApplyNodeChanges(changes, nodes);
      applyNodeChanges(updated);
    },
    [nodes, applyNodeChanges]
  );

  const onEdgesChange = useCallback(
    (changes) => {
      const updated = rfApplyEdgeChanges(changes, edges);
      applyEdgeChanges(updated);
    },
    [edges, applyEdgeChanges]
  );

  const { styledEdges, connectionsBadgeCount } = useEdgeStyling({ nodes, edges, rigCables, selection });
  usePanToSelection(selection, nodes, edges);

  const selectedNodeId = useMemo(() => {
    if (!selection) return null;
    if (selection.type === "boundary") return selection.id;
    if (selection.type === "device") return selection.id;
    return null;
  }, [selection]);

  const { onConnect, onReconnect, onConnectStart, onConnectEnd } = useConnections({
    nodes,
    edges,
    rigCables,
    addConnection,
    updateBoundary,
    reconnectConnection,
    cables: powerCables,
    signalCables,
    trackEvent,
  });

  const selectConnection = useCallback((edgeId) => {
    setSidebarTab("connections");
    setSelection({ type: "edge", id: edgeId });
  }, []);

  const selectBoundary = useCallback((boundaryId) => {
    setSelection({ type: "boundary", id: boundaryId });
  }, []);

  const selectCanvasLabel = useCallback((labelId) => {
    setSelection({ type: "canvasLabel", id: labelId });
  }, []);

  const selectDevice = useCallback((nodeId) => {
    setSelection({ type: "device", id: nodeId });
  }, []);

  // Select + pan without switching sidebar tab (used by parts list clicks)
  const panToDevice = useCallback((nodeId) => {
    setSelection({ type: "device", id: nodeId });
  }, []);

  const panToBoundary = useCallback((boundaryId) => {
    setSelection({ type: "boundary", id: boundaryId });
  }, []);

  const panToEdge = useCallback((edgeId) => {
    setSelection({ type: "edge", id: edgeId });
  }, []);

  const selectPort = useCallback((nodeId, portId) => {
    setSelection({ type: "port", nodeId, portId });
    setSidebarTab("parts");
  }, []);

  useCopyPaste({ nodes, selection, addDevice, addBoundary, addCanvasLabel, selectDevice, selectBoundary, selectCanvasLabel, readOnly });

  const onDragOver = useCallback((event) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = "move";
  }, []);

  const onDrop = useCallback(
    (event) => {
      event.preventDefault();

      const deviceJson = event.dataTransfer.getData("application/json");
      if (!deviceJson) return;

      const data = JSON.parse(deviceJson);
      const position = reactFlowInstance.current.screenToFlowPosition({
        x: event.clientX,
        y: event.clientY,
      });

      if (data.canvasLabel) {
        const node = createCanvasLabelNode(data.canvasLabel, position);
        addCanvasLabel(node);
        trackEvent("canvas_label_added");
      } else if (data.boundary) {
        const node = createBoundaryNode(data.boundary, position);
        addBoundary(node);
        selectBoundary(node.id);
        trackEvent("boundary_added", { direction: data.boundary.direction });
      } else if (Array.isArray(data.devices)) {
        const systemDevices = data.devices;
        if (systemDevices.length === 0) return;

        // Detect wireless system by checking for wireless port pairs
        const wirelessOutputDevices = systemDevices.filter((d) =>
          (d.device_ports || []).some((p) => p.connector === "wireless" && p.direction === "output")
        );
        const wirelessInputDevices = systemDevices.filter((d) =>
          (d.device_ports || []).some((p) => p.connector === "wireless" && p.direction === "input")
        );
        const isWirelessSystem = wirelessOutputDevices.length > 0 && wirelessInputDevices.length > 0;

        // Create nodes for all devices, spaced 280px apart
        const systemNodes = systemDevices.map((device, i) =>
          createDeviceNode(device, { x: position.x + i * 280, y: position.y })
        );

        const newEdges = [];
        const newRigCables = [];

        if (isWirelessSystem) {
          // Auto-wire wireless port pairs
          for (const txDevice of wirelessOutputDevices) {
            const txNode = systemNodes.find((n) => n.data.device.id === txDevice.id);
            const txPort = (txDevice.device_ports || []).find(
              (p) => p.connector === "wireless" && p.direction === "output"
            );
            if (!txNode || !txPort) continue;

            for (const rxDevice of wirelessInputDevices) {
              const rxNode = systemNodes.find((n) => n.data.device.id === rxDevice.id);
              const rxPort = (rxDevice.device_ports || []).find(
                (p) => p.connector === "wireless" && p.direction === "input"
              );
              if (!rxNode || !rxPort) continue;

              newEdges.push({
                id: `e-${txNode.id}-${txPort.id}-${rxNode.id}-${rxPort.id}`,
                source: txNode.id,
                target: rxNode.id,
                sourceHandle: String(txPort.id),
                targetHandle: String(rxPort.id),
                type: "wireless",
                data: {},
              });
            }
          }
        } else {
          // Wired system: auto-connect matching ports between adjacent devices
          for (let i = 0; i < systemNodes.length - 1; i++) {
            const srcDevice = systemDevices[i];
            const dstDevice = systemDevices[i + 1];
            const srcNode = systemNodes[i];
            const dstNode = systemNodes[i + 1];

            const srcOutput = (srcDevice.device_ports || []).find(
              (p) => p.direction === "output" && p.connector !== "wireless"
            );
            const dstInput = (dstDevice.device_ports || []).find(
              (p) => p.direction === "input" && p.connector !== "wireless"
            );

            if (srcOutput && dstInput) {
              const edgeData = {};
              const bestCable = findBestSignalCable(signalCables || [], {
                fromPort: srcOutput,
                toPort: dstInput,
              });
              if (bestCable) {
                const tempId = `rc-${crypto.randomUUID()}`;
                const rigCable = { tempId, cableId: bestCable.id, cable: bestCable, label: null };
                newRigCables.push(rigCable);
                edgeData.cableChain = [{ rigCableTempId: tempId, cableId: bestCable.id, cable: bestCable }];
              }

              newEdges.push({
                id: `e-${srcNode.id}-${srcOutput.id}-${dstNode.id}-${dstInput.id}`,
                source: srcNode.id,
                target: dstNode.id,
                sourceHandle: String(srcOutput.id),
                targetHandle: String(dstInput.id),
                data: edgeData,
              });
            }
          }
        }

        addSystem(systemNodes, newEdges, newRigCables.length > 0 ? newRigCables : undefined);
        selectDevice(systemNodes[0].id);
        trackEvent("system_placed", {
          device_system: systemDevices[0].device_system,
          device_count: systemDevices.length,
          board_device_count: nodes.length + systemDevices.length,
        });
      } else {
        const node = createDeviceNode(data, position);
        addDevice(node);
        selectDevice(node.id);
        trackEvent("device_placed", {
          device_id: data.id,
          device_name: data.name,
          device_type: data.primary_device_type,
          board_device_count: nodes.length + 1,
        });
      }
    },
    [addBoundary, addCanvasLabel, addDevice, addSystem, selectBoundary, selectDevice, nodes, signalCables, trackEvent]
  );

  const onInit = useCallback((instance) => {
    reactFlowInstance.current = instance;
  }, []);

  const onNodeDragStart = useCallback(() => {
    preDragSnapshot.current = { nodes, edges };
  }, [nodes, edges]);

  const onNodeDragStop = useCallback(() => {
    if (preDragSnapshot.current) {
      dragEnd(preDragSnapshot.current);
      preDragSnapshot.current = null;
    }
  }, [dragEnd]);

  const resizeUndo = useMemo(() => ({
    onResizeStart: () => {
      preResizeSnapshot.current = { nodes, edges, rigCables };
    },
    onResizeEnd: () => {
      if (preResizeSnapshot.current) {
        dragEnd(preResizeSnapshot.current);
        preResizeSnapshot.current = null;
      }
    },
  }), [nodes, edges, rigCables, dragEnd]);

  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "z" && !e.shiftKey) {
        e.preventDefault();
        undo();
      }
      if ((e.metaKey || e.ctrlKey) && e.key === "z" && e.shiftKey) {
        e.preventDefault();
        redo();
      }
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [undo, redo]);


  const onEdgeClick = useCallback(
    (_event, edge) => {
      selectConnection(edge.id);
    },
    [selectConnection]
  );

  const onNodeClick = useCallback(
    (_event, node) => {
      if (node.type === "device") {
        setSidebarTab("parts");
        selectDevice(node.id);
      } else if (node.type === "signalBoundary") {
        setSidebarTab("signals");
        selectBoundary(node.id);
      } else if (node.type === "canvasLabel") {
        setSidebarTab("labels");
        selectCanvasLabel(node.id);
      }
    },
    [selectDevice, selectBoundary, selectCanvasLabel]
  );

  const onPaneClick = useCallback(() => {
    setSelection(null);
  }, []);

  const handleCableSelect = useCallback(
    (edgeId, cable, suppliedCable) => {
      assignCable(edgeId, cable, suppliedCable);
    },
    [assignCable]
  );

  const handleDeleteConnection = useCallback(
    (edgeId) => {
      removeConnection(edgeId);
      setSelection(null);
    },
    [removeConnection]
  );

  useEffect(() => {
    if (readOnly && initialState) {
      const state = syncResponseToBoardState(initialState);
      loadState(state);
    }
  }, [readOnly, initialState, loadState]);

  const {
    handleSwitchRig: switchRig, handleNewRig: newRig, handleDuplicateRig,
    handleTogglePrivate, handleUpdateSlug, handleReorderRigs,
    handleRenameRig, handleDeleteRig,
  } = useRigManagement({
    apiClient, currentRigId, setCurrentRigId,
    rigs, setRigs, rigNameRef, stateRef, loadState, saveNow, maxSavedRigs, trackEvent,
  });

  const handleSwitchRig = useCallback((rigId) => {
    setSidebarTab("devices");
    setSelection(null);
    switchRig(rigId);
  }, [switchRig]);

  const handleNewRig = useCallback(() => {
    setSidebarTab("devices");
    setSelection(null);
    newRig();
  }, [newRig]);

  // Notify the outer page (Stimulus controllers, etc.) when the active rig changes
  useEffect(() => {
    if (!currentRigId) return;
    const currentRig = rigs.find((r) => r.id === currentRigId);
    if (!currentRig) return;
    document.dispatchEvent(
      new CustomEvent("rig:changed", {
        detail: {
          id: currentRig.id,
          description: currentRig.description || "",
          notes: currentRig.notes || "",
        },
      })
    );
  }, [currentRigId, rigs]);

  return (
    <>
    <div className="board-editor">
      {!readOnly && (
        <div className="board-editor-sidebar">
          {loading && <p className="board-editor-status">Loading devices...</p>}
          {error && <p className="board-editor-status board-editor-error">Error: {error}</p>}
          {!loading && !error && (
            <SidebarTabs
              activeId={sidebarTab}
              onTabChange={(id) => { setSidebarTab(id); setSelection(null); }}
              tabs={[
                {
                  id: "devices",
                  label: "+ Device",
                  content: <DeviceCatalog devices={devices} />,
                },
                {
                  id: "signals",
                  label: "Signals",
                  content: <SignalsPanel nodes={nodes} updateBoundary={updateBoundary} selectedBoundaryId={selection?.type === "boundary" ? selection.id : null} onSelectBoundary={selectBoundary} />,
                },
                {
                  id: "labels",
                  label: "Labels",
                  content: <LabelsPanel
                    nodes={nodes}
                    onUpdate={updateCanvasLabel}
                    onSwapOrder={swapCanvasLabelOrder}
                    onDelete={removeDevice}
                    onSelect={selectCanvasLabel}
                    selectedLabelId={selection?.type === "canvasLabel" ? selection.id : null}
                  />,
                },
                {
                  id: "connections",
                  label: "Connections",
                  badge: connectionsBadgeCount,
                  content: <ConnectionsPanel
                    nodes={nodes}
                    edges={edges}
                    styledEdges={styledEdges}
                    selectedEdgeId={selection?.type === "edge" ? selection.id : null}
                    onSelectEdge={selectConnection}
                    onAssignCable={handleCableSelect}
                    onAddChainCable={addChainCable}
                    onRemoveChainCable={removeChainCable}
                    onDeleteConnection={handleDeleteConnection}
                    powerCables={powerCables}
                    signalCables={signalCables}
                    cablesLoading={cablesLoading}
                  />,
                },
                {
                  id: "parts",
                  label: "Parts",
                  content: <PartsListPanel
                    authenticated={authenticated}
                    currentRigId={currentRigId}
                    csrfToken={csrfToken}
                    saveStatus={saveStatus}
                    nodes={nodes}
                    edges={edges}
                    selectedDeviceId={selection?.type === "device" ? selection.id : (selection?.type === "port" ? selection.nodeId : null)}
                    selectedBoundaryId={selection?.type === "boundary" ? selection.id : null}
                    selectedEdgeId={selection?.type === "edge" ? selection.id : null}
                    selectedPortId={selection?.type === "port" ? selection.portId : null}
                    onSelectDevice={panToDevice}
                    onSelectBoundary={panToBoundary}
                    onSelectEdge={panToEdge}
                    onUpdateExcludedPort={updateExcludedPort}
                  />,
                },
              ]}
            />
          )}
        </div>
      )}
      <div className="board-editor-main">
        {!readOnly && authenticated && (
          <BoardTabs
            rigs={rigs}
            currentRigId={currentRigId}
            maxSavedRigs={maxSavedRigs}
            onSwitch={handleSwitchRig}
            onNew={handleNewRig}
            onRename={handleRenameRig}
            onReorder={handleReorderRigs}
          />
        )}
        {!readOnly && (
          <BoardToolbar
            authenticated={authenticated}
            currentRig={rigs.find((r) => r.id === currentRigId)}
            atLimit={rigs.filter((r) => !r.over_limit && !r.draft).length >= maxSavedRigs}
            saveStatus={saveStatus}
            onDuplicate={() => handleDuplicateRig(currentRigId)}
            onDelete={() => handleDeleteRig(currentRigId)}
            onTogglePrivate={handleTogglePrivate}
            onUpdateSlug={handleUpdateSlug}
            isAdmin={isAdmin}
            saveNow={saveNow}
            canUndo={canUndo}
            canRedo={canRedo}
            onUndo={undo}
            onRedo={redo}
            showSignup={!authenticated && nodes.length > 0}
            hasCustomDevices={hasCustomDevices}
            nodes={nodes}
            edges={edges}
            trackEvent={trackEvent}
            devMode={devMode}
            currentRigSlug={rigs.find((r) => r.id === currentRigId)?.slug}
          />
        )}
        <div className="board-editor-canvas" ref={reactFlowWrapper}>
        <UpdateDeviceLabelContext.Provider value={readOnly ? null : updateDeviceLabel}>
        <SelectPortContext.Provider value={readOnly ? null : selectPort}>
        <UpdateBoundaryContext.Provider value={updateBoundary}>
        <UpdateCanvasLabelContext.Provider value={readOnly ? null : updateCanvasLabel}>
        <ResizeUndoContext.Provider value={readOnly ? null : resizeUndo}>
        <EdgeClickContext.Provider value={selectConnection}>
        {selectedNodeId && (
          <style>{`.react-flow__node[data-id="${CSS.escape(selectedNodeId)}"] .device-node,
.react-flow__node[data-id="${CSS.escape(selectedNodeId)}"] .signal-boundary-node {
  border-color: var(--selected-border);
  box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.3), 0 1px 3px rgba(0, 0, 0, 0.1);
}`}</style>
        )}
        <ReactFlow
          nodes={nodes}
          edges={styledEdges}
          onNodesChange={readOnly ? undefined : onNodesChange}
          onEdgesChange={readOnly ? undefined : onEdgesChange}
          onConnect={readOnly ? undefined : onConnect}
          onConnectStart={readOnly ? undefined : onConnectStart}
          onConnectEnd={readOnly ? undefined : onConnectEnd}
          onReconnect={readOnly ? undefined : onReconnect}
          onEdgeClick={onEdgeClick}
          onNodeClick={onNodeClick}
          onPaneClick={onPaneClick}
          onDragOver={readOnly ? undefined : onDragOver}
          onDrop={readOnly ? undefined : onDrop}
          onInit={onInit}
          onNodeDragStart={readOnly ? undefined : onNodeDragStart}
          onNodeDragStop={readOnly ? undefined : onNodeDragStop}
          nodeTypes={nodeTypes}
          edgeTypes={edgeTypes}
          nodesDraggable={!readOnly}
          nodesConnectable={!readOnly}
          elementsSelectable={!readOnly}
          deleteKeyCode={readOnly ? null : ["Backspace", "Delete"]}
          fitView
          proOptions={{ hideAttribution: true }}
        >
          <MiniMap />
          <Controls />
          <Background />
          {!readOnly && nodes.length === 0 && (
            <EmptyStateOverlay />
          )}
          {readOnly && (
            <Panel position="top-right" className="board-editor-toolbar">
              <EditAsCopyButton
                authenticated={authenticated}
                canCopy={!authenticated || currentRigCount < maxSavedRigs}
                sourceRigId={initialState?.id}
                apiClient={apiClient}
                nodes={nodes}
                edges={edges}
                rigCables={rigCables}
                rigName={initialState?.name}
                rigDescription={initialState?.description}
                rigNotes={initialState?.notes}
              />
              <ReadOnlyShareLink slug={initialState?.slug} />
            </Panel>
          )}
        </ReactFlow>
        </EdgeClickContext.Provider>
        </ResizeUndoContext.Provider>
        </UpdateCanvasLabelContext.Provider>
        </UpdateBoundaryContext.Provider>
        </SelectPortContext.Provider>
        </UpdateDeviceLabelContext.Provider>
        </div>
        <SignalLegend nodes={nodes} />
      </div>
    </div>
    {readOnly && initialState?.notes && (
      <div className="board-editor-readonly-notes">
        <h2>Notes</h2>
        <div
          className="trix-content"
          dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(initialState.notes) }}
        />
      </div>
    )}
    {readOnly && initialPartsList && (
      <div className="board-editor-readonly-parts">
        <PartsListPanel
          nodes={nodes}
          initialPartsList={initialPartsList}
        />
      </div>
    )}
    </>
  );
}
